/**
 * Which Clerk directory this bundle signs into — declared, not inferred.
 *
 * This is the frontend twin of the backend's `clerk_management.instance_for_host()`,
 * and it exists because the two halves disagreed. The backend resolves the live
 * directory from an exact list and returns it **only in production**; this side
 * used to sniff the hostname:
 *
 *     const isLiveDomain = host === "simplyfundedportal.biz"
 *                       || host.endsWith(".simplyfundedportal.biz");
 *
 * Under that rule `dev.simplyfundedportal.biz` — an environment whose whole
 * purpose is to be broken safely — selected the **LIVE** staff directory. It was
 * survivable only because `REACT_APP_CLERK_PUBLISHABLE_KEY_LIVE` happened to be
 * unset below production, so the `||` fallback reached the test key instead.
 * **That is safety by an absent variable**, and one person adding a build
 * argument removes it silently.
 *
 * A DNS change should not be able to swap identity providers. So the axis is the
 * declared environment, exactly as `portalEnv.js` frames every other
 * environment-keyed decision, and anything unrecognised resolves below
 * production — failing towards the directory that holds no staff identities.
 *
 * ## Why the variables are read here and not passed in
 *
 * Create React App inlines `process.env.REACT_APP_*` at build time by **textual
 * substitution**. `process.env.REACT_APP_FOO` is replaced; `const e =
 * process.env; e.REACT_APP_FOO` is not. Each name therefore has to appear
 * literally, which is what `buildTimeVars()` is for — and why the exported
 * function takes a plain object a test can supply.
 */

import { isProduction } from './portalEnv';

/**
 * The build-time variables this bundle was compiled with.
 *
 * Every `process.env.REACT_APP_*` below must stay a literal reference or CRA
 * will not inline it and the value reaches the browser as `undefined`.
 *
 * @returns {{portalEnv: string|undefined, test: string|undefined, live: string|undefined}}
 */
const buildTimeVars = () => ({
  portalEnv: process.env.REACT_APP_PORTAL_ENV,
  test: process.env.REACT_APP_CLERK_PUBLISHABLE_KEY,
  live: process.env.REACT_APP_CLERK_PUBLISHABLE_KEY_LIVE,
});

/**
 * The Clerk publishable key this bundle should use, or `null` if none is set.
 *
 * The live key is selectable **only** when the declared environment is
 * `production`. The hostname is not consulted and must not be.
 *
 * @param {{portalEnv?: string, test?: string, live?: string}} [vars]
 *        build-time variables; defaults to this bundle's own.
 * @returns {string|null}
 */
export function clerkPublishableKey(vars = buildTimeVars()) {
  const { portalEnv, test, live } = vars ?? {};

  if (isProduction(portalEnv)) {
    // Known trap, deliberately left as it was: an unset live key falls back to
    // the test key. The backend refuses outright in that case, so the two halves
    // disagree and every authenticated request answers 401 — loud rather than
    // silent, which is the safe direction. Changing it is a production
    // behaviour change and belongs in its own commit.
    return live || test || null;
  }

  return test || null;
}
